import * as React from "react";
import { useState, ReactNode, forwardRef } from "react";
import { APPLY_URL } from "@/lib/apply-url";
import { track } from "@/lib/analytics";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink, Home } from "lucide-react";

type ApplyLinkProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  /** Override the application destination. Defaults to the central APPLY_URL. */
  href?: string;
};

/** Extract a readable domain from a URL */
const getDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
};

/**
 * Wraps any clickable element. On click it shows a "leaving site" disclaimer
 * dialog before redirecting to the external application portal.
 *
 * Renders a real <button type="button"> for correct semantics and keyboard
 * support. When wrapped in `<Button asChild>`, Radix Slot clones this
 * component and forwards className/ref to the underlying button.
 */
const ApplyLink = forwardRef<HTMLButtonElement, ApplyLinkProps>(
  ({ children, className, href, ...rest }, ref) => {
    const [open, setOpen] = useState(false);
    const destinationUrl = href ?? APPLY_URL;
    const destination = getDomain(destinationUrl);

    return (
      <>
        <button
          {...rest}
          ref={ref}
          type="button"
          className={className ?? "inline-flex"}
          onClick={() => {
            track("apply_click", { name: "Lead", kind: "standard" }, { destination });
            setOpen(true);
          }}
        >
          {children}
        </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              You're Leaving Hiler Mortgage
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-base leading-relaxed text-muted-foreground">
                  You are about to visit an external website to continue your mortgage application. Hiler Mortgage is not responsible for the content, privacy practices, security, or accessibility of external sites.
                </p>

                <p className="text-sm font-medium text-foreground break-all">
                  Destination: {destination}
                </p>

                <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-semibold text-foreground">Equal Housing Opportunity</span>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Hiler Mortgage is an Equal Housing Opportunity company. We do business in accordance with the federal Fair Housing Act and Equal Credit Opportunity Act and do not discriminate on the basis of race, color, religion, national origin, sex, marital status, age, familial status, disability, receipt of public assistance, or because a person has exercised rights under consumer credit laws.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on Hiler Mortgage</AlertDialogCancel>
            <AlertDialogAction asChild>
              <a
                href={destinationUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("apply_outbound", { name: "Lead", kind: "standard" }, { destination })
                }
              >
                Continue to Application
              </a>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
    );
  },
);
ApplyLink.displayName = "ApplyLink";

export default ApplyLink;
